import type { EChartsOption } from 'echarts'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import type { Aggregation, IWidget } from '@/interfaces/components/dashboard/widget'
import type { AggregationKey } from '@/interfaces/api/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { useI18n } from '@/composable/useI18n'
import {
  getSchemaFieldLabel,
  resolveDataFieldUnit,
  resolveWidgetDataFields,
} from '@/utils/sensor-schema-fields'
import { indicatorSourceKeyFromWidget, isGroupWidget } from '@/utils/dashboard/widget-source'
import { formatWidgetMetricValue } from '@/utils/dashboard/format-widget-metric'
import { storeToRefs } from 'pinia'

export const PIE_CHART_ACCENT_COLOR = '#12B76A'

const PIE_SLICE_COLORS = [
  '#12B76A',
  '#123add',
  '#80CAEE',
  '#F79009',
  '#F04438',
  '#7A5AF8',
  '#EE46BC',
  '#15B79E',
  '#6172F3',
]

const PIE_LABEL_DARK = '#000A1E'
const PIE_LABEL_LIGHT = '#FFFFFF'

function getContrastLabelColor(hexColor: string): string {
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? PIE_LABEL_DARK : PIE_LABEL_LIGHT
}

function buildSliceLabelConfig(sliceColor: string, value: number, dataField: string, unit: string) {
  const labelColor = getContrastLabelColor(sliceColor)
  const config = {
    show: true,
    position: 'inside' as const,
    formatter: formatWidgetMetricValue(value, dataField, unit),
    color: labelColor,
    textStyle: {
      color: labelColor,
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
    },
  }
  return {
    label: config,
    emphasis: {
      label: config,
    },
  }
}

export function usePieChartWidget() {
  const store = useCompanyDashboardStore()
  const { indicatorSources } = storeToRefs(store)
  const { t, te } = useI18n()

  const getOption = (widget: IWidget): EChartsOption => {
    const aggregation = (
      isGroupWidget(widget) ? 'last' : (widget.config.aggregation || 'mean')
    ) as Aggregation
    const aggregationKey = aggregation as AggregationKey
    const dataFields = resolveWidgetDataFields(widget.config)

    const slices: Array<{
      value: number
      name: string
      unit: string
      dataField: string
      itemStyle: { color: string }
      label: ReturnType<typeof buildSliceLabelConfig>['label']
      emphasis: ReturnType<typeof buildSliceLabelConfig>['emphasis']
    }> = []

    let colorIndex = 0

    dataFields.forEach((dataField) => {
      const sourceKey = indicatorSourceKeyFromWidget(widget, dataField)
      if (!sourceKey) return
      const source = indicatorSources.value[sourceKey]
      const value = source?.aggregations?.[aggregationKey] ?? source?.aggregations?.last ?? null

      if (value === null) return

      const sliceColor = PIE_SLICE_COLORS[colorIndex % PIE_SLICE_COLORS.length]
      const unit = resolveDataFieldUnit(dataField, source?.unit ?? '')
      const labelConfig = buildSliceLabelConfig(sliceColor, value, dataField, unit)
      const fieldLabel = isGroupWidget(widget)
        ? t(`custom_dashboards.widget_modal.group_fields.${dataField}`)
        : getSchemaFieldLabel(dataField, t, te)

      slices.push({
        value,
        name: fieldLabel,
        unit,
        dataField,
        itemStyle: { color: sliceColor },
        label: labelConfig.label,
        emphasis: labelConfig.emphasis,
      })
      colorIndex += 1
    })

    const legendRows = Math.max(1, Math.ceil(slices.length / 3))
    const pieCenterY = slices.length === 0 ? '50%' : `${46 - (legendRows - 1) * 3}%`

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
          if (Array.isArray(params)) return ''
          const data = params.data as { unit?: string; dataField?: string } | undefined
          const value = params.value
          if (typeof value !== 'number') return `${params.name ?? ''}: ${value ?? ''}`
          const formatted = formatWidgetMetricValue(value, data?.dataField ?? '', data?.unit ?? '')
          return `${params.name}: ${formatted}`
        },
      },
      legend: {
        bottom: 4,
        left: 'center',
        icon: 'rect',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: { fontSize: 10, color: '#64748B' },
      },
      series: [
        {
          type: 'pie',
          radius: ['24%', '74%'],
          center: ['50%', pieCenterY],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 0 },
          label: { show: true, position: 'inside' },
          labelLine: { show: false },
          data: slices,
        },
      ],
    }
  }

  return { getOption, ACCENT_COLOR: PIE_CHART_ACCENT_COLOR }
}
